import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ScoreRing, ScoreBar } from "@/components/ScoreCard";
import { buildFilterCSS } from "@/data/cinema";
import { useApp } from "@/context/AppContext";
import { useLang, useCinema } from "@/i18n/LanguageContext";

// صفحة النتيجة: نتيجة عامة + درجات + قوة/مشاكل + قبل وبعد + أزرار
export default function ResultView({ result, image, filterId, onReanalyze }) {
  const navigate = useNavigate();
  const { setActiveFilterId } = useApp();
  const { t } = useLang();
  const { FILTER_BY_ID } = useCinema();
  const s = result.scores;
  const filter = FILTER_BY_ID[filterId];
  const afterCSS = filter ? buildFilterCSS({ ...filter.base, intensity: 70 }) : "none";

  const goCameraWithFilter = () => {
    setActiveFilterId(filterId);
    navigate("/app/camera");
  };

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="result-view">
      <div className="glass glass-blue rounded-3xl p-5 flex items-center gap-5">
        <ScoreRing value={s.overall} size={120} color="#d9b46a" testId="score-overall" />
        <div className="flex-1">
          <h3 className="font-display text-xl gold-gradient-text mb-1">{t("overallScore")}</h3>
          <p className="text-xs text-slate-400 leading-relaxed">{t("overallDesc")}</p>
        </div>
      </div>

      <div className="glass rounded-3xl p-5 space-y-3">
        <h4 className="font-display text-slate-200 mb-2">{t("detailedScores")}</h4>
        <ScoreBar label={t("compositionScore")} value={s.composition} color="#d9b46a" />
        <ScoreBar label={t("lightingScore")} value={s.lighting} color="#4ea8ff" />
        <ScoreBar label={t("colorsScore")} value={s.colors} color="#e78fa3" />
        <ScoreBar label={t("cameraScore")} value={s.camera} color="#7bd88f" />
      </div>

      {image && (
        <div className="glass rounded-3xl p-4">
          <h4 className="font-display text-slate-200 mb-3">{t("beforeAfter")}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <img src={image} alt="before" className="w-full h-32 object-cover rounded-xl" />
              <p className="text-center text-[11px] text-slate-400 mt-1">{t("before")}</p>
            </div>
            <div>
              <img src={image} alt="after" className="w-full h-32 object-cover rounded-xl" style={{ filter: afterCSS }} />
              <p className="text-center text-[11px] text-gold mt-1">{t("afterFiltered")}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        <ListCard title={t("strengths")} icon="fa-circle-check" color="#7bd88f" items={result.strengths} />
        <ListCard title={t("issues")} icon="fa-triangle-exclamation" color="#f0b93a" items={result.issues} />
        <ListCard title={t("fixes")} icon="fa-screwdriver-wrench" color="#4ea8ff" items={result.fixes} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button data-testid="result-to-camera" onClick={goCameraWithFilter} className="glass glass-blue rounded-2xl py-3 text-sm text-ai flex items-center justify-center gap-2">
          <i className="fa-solid fa-vr-cardboard" /> {t("arCameraBtn")}
        </button>
        <button data-testid="result-apply-template" onClick={() => navigate("/app/templates")} className="glass rounded-2xl py-3 text-sm text-pink-300 flex items-center justify-center gap-2" style={{ borderColor: "rgba(231,143,163,0.4)" }}>
          <i className="fa-solid fa-clapperboard" /> {t("applyTemplateBtn")}
        </button>
        <button data-testid="result-apply-filter" onClick={goCameraWithFilter} className="glass gold-border rounded-2xl py-3 text-sm text-gold flex items-center justify-center gap-2">
          <i className="fa-solid fa-wand-magic-sparkles" /> {t("applyFilterBtn")}
        </button>
        <button data-testid="result-reanalyze" onClick={onReanalyze} className="btn-gold rounded-2xl py-3 text-sm font-bold flex items-center justify-center gap-2">
          <i className="fa-solid fa-rotate-right" /> {t("reanalyze")}
        </button>
      </div>
    </motion.div>
  );
}

function ListCard({ title, icon, color, items }) {
  return (
    <div className="glass rounded-2xl p-4">
      <h4 className="flex items-center gap-2 font-display text-slate-200 mb-2">
        <i className={`fa-solid ${icon}`} style={{ color }} /> {title}
      </h4>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
            <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
